// Optimized: Import only the icons we use instead of the entire library
// This reduces bundle size from 37MB to ~100KB
import styles from "@styles/Icon.module.css"
import { getIconComponent } from "./iconRegistry"

export enum IconName {
  CrossLarge = "IconCrossLarge",
  DotGrid1x3Vertical = "IconDotGrid1x3Vertical",
  MagnifyingGlass = "IconMagnifyingGlass",
  Paperclip2 = "IconPaperclip2",
  PlusLarge = "IconPlusLarge",
  TrashPermanently = "IconTrashPermanently",
  TrashCanSimple = "IconTrashCanSimple",
  ArrowsRepeatCircle = "IconArrowsRepeatCircle",
  ArrowRounded = "IconArrowRounded",
  TriangleExclamation = "IconExclamationTriangle",
  CircleX = "IconCircleX",
  CircleCheck = "IconCircleCheck",
  CirclePlus = "IconCirclePlus",
  ChevronGrabberVertical = "IconChevronGrabberVertical",
  SquareArrowTopRight = "IconSquareArrowTopRight",
  ChevronDownSmall = "IconChevronDownSmall",
  ChevronLeftSmall = "IconChevronLeftSmall",
  ChevronTopSmall = "IconChevronTopSmall",
  ChevronRightSmall = "IconChevronRightSmall",
  ChevronBottom = "IconChevronBottom",
  ChevronLeft = "IconChevronLeft",
  ChevronTop = "IconChevronTop",
  ChevronRight = "IconChevronRight",
  PeopleCircle = "IconPeopleCircle",
  Puzzle = "IconPuzzle",
  ArrowBoxLeft = "IconArrowBoxLeft",
  Email3 = "IconEmail3",
  Calendar1 = "IconCalendar1",
  BubbleAnnotation6 = "IconBubbleAnnotation6",
  IconGroup1 = "IconGroup1",
  MapPin = "IconMapPin",
  Clock = "IconClock",
  SquareChecklist = "IconSquareChecklist",
  ArrowRight = "IconArrowRight",
  Loader = "IconLoader",
  Payment = "IconPayment",
  SquarePlaceholderDashed = "IconSquarePlaceholderDashed",
  ClockDefault = "IconClockDefault",
  Checkmark2 = "IconCheckmark2",
}

export enum ActiveIconName {
  SquareChecklist = "IconSquareChecklist",
  SettingsGear = "IconSettingsGear1",
  SquarePlaceholderDashed = "IconSquarePlaceholderDashed",
  Retouch = "IconRetouch",
  CircleX = "IconCircleX",
  CircleCheck = "IconCircleCheck",
}

export enum IconSize {
  xSmall = 12,
  Small = 16,
  MediumSmall = 20,
  Medium = 24,
  Large = 32,
  xLarge = 64,
}

export enum IconColor {
  White = "white",
  Black = "black",
  Red = "red",
  Green = "green",
}

export function Icon({
  name,
  size = IconSize.Medium,
  color = IconColor.Black,
  customColor = null,
  isFilled = false,
  spinning = false,
  strokeWidth = 2,
}: {
  name: IconName | ActiveIconName
  size?: IconSize
  color?: IconColor
  customColor?: string
  isFilled?: boolean
  spinning?: boolean
  strokeWidth?: number | string
}) {
  const IconComponent = getIconComponent(name, isFilled)

  if (!IconComponent) {
    console.warn(`Icon not found: ${name}`)
    return (
      <div
        style={{
          width: size,
          height: size,
          display: "inline-block",
        }}
      />
    )
  }

  const finalColor = customColor || color
  // Note: radius and strokeWidth are baked into the @central-icons-react package variants
  // The package name includes: round-filled-radius-0-stroke-2
  // - radius-0 = sharp corners (can't be changed via props)
  // - stroke-2 = 2px stroke width (can't be changed via props)
  // To change these, you need to use a different package variant

  return (
    <div
      className={`${styles.iconCustom} ${
        size === IconSize.xSmall
          ? styles.xSmall
          : size === IconSize.Small
            ? styles.small
            : size === IconSize.MediumSmall
              ? styles.mediumSmall
              : size === IconSize.Medium
                ? styles.medium
                : size === IconSize.Large
                  ? styles.large
                  : styles.xLarge
      } ${spinning && styles.spinning}`}
      style={{
        color: finalColor,
        stroke: isFilled ? "none" : finalColor,
        ...(strokeWidth &&
          !isFilled && {
            // Override stroke-width via CSS if provided (may not work with pre-rendered paths)
            ["--icon-stroke-width" as string]: strokeWidth,
          }),
      }}
    >
      <IconComponent size={size} color={finalColor} />
    </div>
  )
}
